import { useQuery } from "@tanstack/react-query";
import axios from "axios";

export interface VREvent {
  id: number;
  slug: string;
  title: string;
  short_description: string | null;
  description: string | null;
  event_date: string | null;
  location: string | null;
  category: string | null;
  client_name: string | null;
  vr_equipment: string | null;
  cover_image: string | null;
  gallery_images: string[];
  video_url: string | null;
  external_url: string | null;
  is_featured: boolean;
}

export function useEvents(limit?: number) {
  return useQuery({
    queryKey: ["events", limit ?? "all"],
    queryFn: async () => {
      const res = await axios.get("/.netlify/functions/get-events", {
        params: limit ? { limit } : undefined,
      });
      return (res.data?.data ?? []) as VREvent[];
    },
  });
}

export function useEvent(slug?: string) {
  return useQuery({
    queryKey: ["event", slug],
    enabled: !!slug,
    queryFn: async () => {
      const res = await axios.get("/.netlify/functions/get-events", { params: { slug } });
      return (res.data?.data ?? null) as VREvent | null;
    },
  });
}

export function formatEventDate(date: string | null) {
  if (!date) return null;
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/** Turn a YouTube/Vimeo URL into an embeddable src, or null if unsupported. */
export function toEmbedUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") return `https://www.youtube.com/embed${u.pathname}`;
    if (host.endsWith("youtube.com")) {
      if (u.pathname.startsWith("/embed/") || u.pathname.startsWith("/shorts/")) {
        return `https://www.youtube.com/embed/${u.pathname.split("/").pop()}`;
      }
      const id = u.searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host.endsWith("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean)[0];
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
    return null;
  } catch {
    return null;
  }
}
